import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
  ActionCodeSettings,
  User
} from 'firebase/auth';
import { doc, getDoc, serverTimestamp, updateDoc, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { auth, db, storage, functions } from '../../../services/firebase/config';
import { ensureUserProfile as ensureUserProfileCallable } from '../../../services/firebase/functions';
import { generateRandomUsername } from './username.service';

// Types
export interface SignupData {
  email: string;
  password: string;
  displayName?: string;
  // Code d'accès beta saisi à l'étape « Join the beta » — validé serveur
  // (redeemBetaCode pose le claim `beta` exigé par ensureUserProfile pour
  // créer le doc users/{uid}).
  betaCode?: string;
}

export interface AuthResult {
  success: boolean;
  user?: {
    uid: string;
    email: string | null;
    displayName: string | null;
    emailVerified: boolean;
    [key: string]: any;
  };
  error?: string;
}

export interface UserDataResult {
  success: boolean;
  userData?: Record<string, any>;
  error?: string;
}

// Error message mapping
const getAuthErrorMessage = (code: string): string => {
  const errorMessages: Record<string, string> = {
    'auth/email-already-in-use': 'This email address is already in use',
    'auth/weak-password': 'Password must be at least 6 characters long',
    'auth/invalid-email': 'Invalid email address',
    'auth/user-not-found': 'No account found with this email address',
    'auth/wrong-password': 'Incorrect password',
    'auth/too-many-requests': 'Too many attempts. Try again later',
    'auth/popup-closed-by-user': 'Login was cancelled',
    'auth/account-exists-with-different-credential': 'An account already exists with this email',
    // Compte suspendu par la modération (issue #75) : message ferme — le
    // compte Auth est désactivé serveur, la tentative de reconnexion doit
    // être décourageante et sans ambiguïté.
    'auth/user-disabled': 'This account has been permanently suspended for violating our content policy. Any further attempt may be reported to law enforcement.'
  };
  return errorMessages[code] || 'An error occurred';
};

// La callable écrit le profil et la réservation dans une seule transaction.
// Elle est aussi idempotente pour les bootstraps Auth concurrents et migre les
// seuls profils legacy sans username, sans réattribuer un profil déjà nommé.
const ensureUserDocument = async (
  uid: string,
  requestedUsername?: string | null
): Promise<Record<string, any>> => {
  let username = requestedUsername || await generateRandomUsername();
  let lastError: any = null;

  for (let attempt = 0; attempt < 5; attempt++) {
    try {
      await ensureUserProfileCallable({ username });
      const userDocSnap = await getDoc(doc(db, 'users', uid));
      if (!userDocSnap.exists()) throw new Error('User profile missing after creation');
      return userDocSnap.data();
    } catch (error: any) {
      lastError = error;
      if (error?.code !== 'functions/already-exists') throw error;
      username = await generateRandomUsername();
    }
  }

  throw lastError || new Error('Failed to reserve username after 5 attempts');
};

// Sign up with email/password
export const signUpWithEmail = async (data: SignupData): Promise<AuthResult> => {
  const { email, password, displayName, betaCode } = data;

  // Le claim `beta` (posé par redeemBetaCode) est exigé par ensureUserProfile
  // pour créer le doc users/{uid} — sans code, le compte Auth resterait inerte.
  if (!betaCode) {
    return { success: false, error: 'Access code required' };
  }

  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    const username = await generateRandomUsername();
    const name = username || displayName || email.split('@')[0];

    await updateProfile(user, { displayName: name });

    // Valide le code serveur et pose le claim `beta: true` ; le token est
    // rafraîchi pour que ensureUserProfile le voie à la création du profil.
    await redeemBetaCode(betaCode);

    // Set language for verification email
    auth.languageCode = 'en';

    // Send verification email with redirect URL
    const actionCodeSettings: ActionCodeSettings = {
      url: 'https://rabbit-46dfd.web.app/projects',
      handleCodeInApp: true
    };
    await sendEmailVerification(user, actionCodeSettings);

    const userData = await ensureUserDocument(user.uid, username);

    return {
      success: true,
      user: {
        uid: user.uid,
        email: user.email,
        displayName: userData.displayName || name,
        username: userData.username,
        emailVerified: false
      }
    };
  } catch (error: any) {
    if (typeof error?.code === 'string' && error.code.startsWith('functions/')) {
      return { success: false, error: getCallableErrorMessage(error) };
    }
    return { success: false, error: getAuthErrorMessage(error.code) };
  }
};

// Sign in with email/password
// `betaCode` : seconde chance pour un compte Auth créé sans accès beta
// (redeem interrompu au signup, compte créé hors UI) — le code est validé
// serveur, le claim posé, puis le doc users/{uid} créé normalement.
export const signInWithEmail = async (email: string, password: string, betaCode?: string): Promise<AuthResult> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    let userData: Record<string, any>;
    try {
      userData = await ensureUserDocument(user.uid);
    } catch (error: any) {
      // Compte Auth sans accès beta : ensureUserProfile refuse la création.
      if (error?.code !== 'permission-denied' && error?.code !== 'functions/permission-denied') {
        throw error;
      }
      if (!betaCode) {
        return { success: false, error: 'This account has no beta access' };
      }
      try {
        await redeemBetaCode(betaCode);
      } catch (redeemError: any) {
        return { success: false, error: getCallableErrorMessage(redeemError) };
      }
      userData = await ensureUserDocument(user.uid);
    }

    return {
      success: true,
      user: {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        emailVerified: user.emailVerified,
        ...userData
      }
    };
  } catch (error: any) {
    return { success: false, error: getAuthErrorMessage(error.code) };
  }
};

// Build a consistent AuthResult from a Firebase User after Google sign-in.
const buildGoogleAuthResult = async (firebaseUser: User): Promise<AuthResult> => {
  const userData = await ensureUserDocument(firebaseUser.uid);

  return {
    success: true,
    user: {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      emailVerified: firebaseUser.emailVerified,
      ...userData
    }
  };
};

// Sign in with Google using the popup flow.
// This is simpler and more reliable than redirect across browsers and origins.
// Nouveau compte (pas de doc users/{uid}) : le code d'accès beta est validé
// serveur et pose le claim `beta: true` avant la création transactionnelle du
// profil par ensureUserProfile.
export const signInWithGoogle = async (betaCode?: string): Promise<AuthResult> => {
  console.log('[AUTH] signInWithGoogle called');
  const provider = new GoogleAuthProvider();

  try {
    const result = await signInWithPopup(auth, provider);
    const firebaseUser = result.user;

    const userDocSnap = await getDoc(doc(db, 'users', firebaseUser.uid));
    if (!userDocSnap.exists()) {
      if (!betaCode) {
        return { success: false, error: 'Access code required' };
      }
      try {
        await redeemBetaCode(betaCode);
      } catch (error: any) {
        return { success: false, error: getCallableErrorMessage(error) };
      }
    }

    return await buildGoogleAuthResult(firebaseUser);
  } catch (error: any) {
    console.error('[AUTH] Google sign-in error:', error.code, error.message, error);
    if (error?.code === 'permission-denied' || error?.code === 'functions/permission-denied') {
      return { success: false, error: 'This account has no beta access' };
    }
    return { success: false, error: getAuthErrorMessage(error.code) };
  }
};

// ---------------------------------------------------------------------------
// Popin unifiée « email-first » (/login) — callable functions/emailAuth.js.
// Le message d'erreur serveur est déjà user-safe (anglais) : on le propage
// tel quel pour les erreurs functions/*.
// ---------------------------------------------------------------------------

const getCallableErrorMessage = (error: any): string => {
  if (typeof error?.code === 'string' && error.code.startsWith('functions/') && error.message) {
    return error.message;
  }
  return 'An error occurred';
};

const checkEmailAccountFn = httpsCallable<
  { email: string },
  { exists: boolean; providers: string[] }
>(functions, 'checkEmailAccount');

export interface EmailAccountCheck {
  success: boolean;
  exists?: boolean;
  providers?: string[];
  error?: string;
}

// Routing email-first : le compte existe-t-il, et par quels providers ?
export const checkEmailAccount = async (email: string): Promise<EmailAccountCheck> => {
  try {
    const result = await checkEmailAccountFn({ email });
    return { success: true, exists: result.data.exists, providers: result.data.providers };
  } catch (error: any) {
    return { success: false, error: getCallableErrorMessage(error) };
  }
};

// ---------------------------------------------------------------------------
// Gate beta — callables functions/betaAccess.js. Le code d'accès n'est plus
// comparé côté client (il était en clair dans le bundle) : validateBetaCode
// arme l'étape « Join the beta » (UX), redeemBetaCode pose le custom claim
// `beta: true` exigé par ensureUserProfile pour créer le doc users/{uid}
// — c'est lui le vrai verrou.
// ---------------------------------------------------------------------------

const validateBetaCodeFn = httpsCallable<
  { code: string },
  { valid: boolean }
>(functions, 'validateBetaCode');

const redeemBetaCodeFn = httpsCallable<
  { code: string },
  { success: boolean }
>(functions, 'redeemBetaCode');

export interface BetaCodeValidation {
  success: boolean;
  valid?: boolean;
  error?: string;
}

// Étape « Join the beta » de la popin /login — gate UX côté serveur.
export const validateBetaCode = async (code: string): Promise<BetaCodeValidation> => {
  try {
    const result = await validateBetaCodeFn({ code });
    return { success: true, valid: result.data.valid };
  } catch (error: any) {
    return { success: false, error: getCallableErrorMessage(error) };
  }
};

// Pose le claim `beta: true` puis rafraîchit l'idToken pour que
// ensureUserProfile le voie immédiatement à la création du profil.
// Propagation de l'erreur functions/* (message user-safe serveur).
const redeemBetaCode = async (code: string): Promise<void> => {
  await redeemBetaCodeFn({ code });
  if (auth.currentUser) {
    await auth.currentUser.getIdToken(true);
  }
};

// Accès beta : claim `beta` OU doc users/{uid} pré-existant (comptes
// antérieurs au gate, sans claim — leur doc existe déjà). Gate de
// ProtectedRoute : un compte Auth créé en contournant l'UI (API Firebase
// publique) n'a ni claim ni doc → accès refusé.
export const hasBetaAccess = async (firebaseUser: User): Promise<boolean> => {
  // Force refresh : juste après redeemBetaCode (inscription beta), le token
  // en cache ne porte pas encore le claim `beta` — sans refresh, le gate de
  // ProtectedRoute lisait un token périmé et refusait un compte valide.
  const tokenResult = await firebaseUser.getIdTokenResult(true);
  if (tokenResult.claims.beta === true) {
    return true;
  }
  const userDocSnap = await getDoc(doc(db, 'users', firebaseUser.uid));
  return userDocSnap.exists();
};

// Compte suspendu (issue #75 — enforcement de la politique de contenu) :
// champ `suspended` du doc users/{uid}, posé serveur seul par le chantier
// modération. Gate de ProtectedRoute : un compte suspendu est signé hors
// session et voit l'écran « Account suspended ». Doc absent = non suspendu.
export const getUserSuspended = async (uid: string): Promise<boolean> => {
  const userDocSnap = await getDoc(doc(db, 'users', uid));
  return userDocSnap.exists() && userDocSnap.data().suspended === true;
};

// Abonnement TEMPS RÉEL au statut de suspension (issue #75) : la suspension
// peut survenir en pleine session (un run flaggé déclenche les strikes
// serveur pendant que l'utilisateur est sur le canvas) — sans listener,
// elle n'était détectée qu'au rechargement. ProtectedRoute y souscrit tant
// que l'utilisateur est authentifié : suspended → true = écran plein +
// sign-out immédiat. Retourne l'unsubscribe (A21).
export const subscribeUserSuspended = (
  uid: string,
  onChange: (suspended: boolean) => void
): (() => void) => {
  return onSnapshot(
    doc(db, 'users', uid),
    (snap) => onChange(snap.exists() && snap.data().suspended === true),
    (error) => console.error('[AUTH] suspended listener error:', error)
  );
};

// Sign out
export const signOutUser = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    await signOut(auth);
    // Réarme la popin d'onboarding : la prochaine connexion la ré-affichera
    // même dans le même onglet (le flag vit en sessionStorage).
    sessionStorage.removeItem('rabbitOnboardingShown');
    return { success: true };
  } catch (error) {
    return { success: false, error: 'Error during logout' };
  }
};

// Get user data from Firestore
export const getUserData = async (uid: string): Promise<UserDataResult> => {
  try {
    const userDocSnap = await getDoc(doc(db, 'users', uid));
    if (userDocSnap.exists()) {
      return { success: true, userData: userDocSnap.data() };
    }
    return { success: false, error: 'User not found' };
  } catch (error) {
    return { success: false, error: 'Error retrieving data' };
  }
};

// Resend verification email
export const resendVerificationEmail = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'No user logged in' };
    }

    auth.languageCode = 'en';
    const actionCodeSettings: ActionCodeSettings = {
      url: 'https://rabbit-46dfd.web.app/projects',
      handleCodeInApp: true
    };
    await sendEmailVerification(user, actionCodeSettings);
    return { success: true };
  } catch (error: any) {
    if (error.code === 'auth/too-many-requests') {
      return { success: false, error: 'Too many attempts. Try again later' };
    }
    return { success: false, error: 'Error sending verification email' };
  }
};

// Update user profile photo
export const updateUserProfilePhoto = async (file: File): Promise<{ success: boolean; photoURL?: string; error?: string }> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'No user logged in' };
    }

    const storageRef = ref(storage, `user_profiles/${user.uid}/profile.jpg`);
    await uploadBytes(storageRef, file);
    const photoURL = await getDownloadURL(storageRef);

    await updateProfile(user, { photoURL });
    await updateDoc(doc(db, 'users', user.uid), {
      photoURL,
      updatedAt: serverTimestamp()
    });

    return { success: true, photoURL };
  } catch (error: any) {
    return { success: false, error: error.message || 'Error updating profile photo' };
  }
};

// Export auth instance and onAuthStateChanged for useAuth hook
export { auth, onAuthStateChanged };

// Export ensureUserDocument so useAuth can create/migrate the Firestore user
// document after a redirect-based sign-in (Google).
export { ensureUserDocument };

// Re-export username helpers for convenience
export { updateUsername, validateUsername, isUsernameAvailable } from './username.service';
